import React, { useState, useMemo, useRef } from "react";
import { playRight, playWrong, playFanfare } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CalendarDays, Check, X, Crown, Flame, Award, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { StaffViewer } from "@/components/StaffViewer";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";
import { playPitch } from "@/lib/audio";
import { LETTERS } from "@/lib/music";
import { api } from "@/lib/api";
import { makeDailyChallenge, todayStr, STREAK_TIERS, readStreakRewards, unlockStreakRewards } from "@/lib/trainerStore";

function letterOf(pitch) {
  return pitch.replace(/[0-9]/g, "").replace(/[#b]/g, "");
}
function fmtMs(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}
function prettyDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  } catch (e) {
    return d;
  }
}

function DailyLeaderboard({ date, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  React.useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get("/trainer/daily", { params: { date } })
      .then((r) => live && setData(r.data))
      .catch(() => {})
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [date, refreshKey]);
  const entries = data?.entries || [];
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="daily-leaderboard">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Crown size={16} /></span>
        <div>
          <p className="font-serif text-xl">Today's leaderboard</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ranked by score, then time</p>
        </div>
      </div>
      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="daily-lb-loading">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="daily-lb-empty">No entries yet today — be the first to set a time!</p>
      ) : (
        <div className="mt-4 space-y-1.5" data-testid="daily-lb-list">
          {entries.map((e) => (
            <div key={e.rank} data-testid={`daily-lb-row-${e.rank}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${e.is_me ? "bg-primary/10 ring-1 ring-primary/40" : "bg-background"}`}>
              <span className={`w-6 text-center font-serif text-lg ${e.rank <= 3 ? "text-primary" : "text-muted-foreground"}`}>{e.rank}</span>
              <span className="flex-1 truncate text-sm font-medium">{e.name}</span>
              <span className="text-sm text-foreground">{e.score}/{e.total}</span>
              <span className="w-14 text-right font-mono text-xs text-muted-foreground">{fmtMs(e.time_ms)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DailyChallenge() {
  const date = todayStr();
  const notes = useMemo(() => makeDailyChallenge(date, 10), [date]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [done, setDone] = useState(false);
  const [name, setName] = useState(() => {
    try { return localStorage.getItem("jt_trainer_name") || ""; } catch (e) { return ""; }
  });
  const [posting, setPosting] = useState(false);
  const [postedRank, setPostedRank] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [streak, setStreak] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem("jt_daily_streak") || "{}") || {};
      const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      if (d.lastDate === date || d.lastDate === y) return d.count || 0;
    } catch (e) {}
    return 0;
  });
  const startRef = useRef(null);
  const elapsedRef = useRef(0);
  const [rewards, setRewards] = useState(() => readStreakRewards());

  const updateStreak = () => {
    let data = {};
    try { data = JSON.parse(localStorage.getItem("jt_daily_streak") || "{}") || {}; } catch (e) {}
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    let count;
    if (data.lastDate === date) count = data.count || 1;
    else if (data.lastDate === yesterday) count = (data.count || 0) + 1;
    else count = 1;
    try { localStorage.setItem("jt_daily_streak", JSON.stringify({ lastDate: date, count })); } catch (e) {}
    setStreak(count);
    const newly = unlockStreakRewards(count);
    if (newly.length) setRewards(readStreakRewards());
    if (data.lastDate !== date) {
      if (newly.length) {
        try { confetti({ particleCount: 150, spread: 95, origin: { y: 0.6 } }); } catch (e) {}
        newly.forEach((t) => toast.success(`🏅 Reward unlocked: ${t.label} — ${t.days}-day streak!`));
      } else if (count >= 2) {
        try { confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 } }); } catch (e) {}
        toast.success(`${count}-day daily streak! 🔥`);
      }
    }
    return count;
  };
  const alreadyPlayed = (() => {
    try { return localStorage.getItem("jt_daily_done") === date; } catch (e) { return false; }
  })();

  const current = notes[idx];

  const guess = (letter) => {
    if (feedback || done) return;
    if (startRef.current == null) startRef.current = Date.now();
    const correct = letterOf(current.pitch);
    const isRight = letter === correct;
    setChosen(letter);
    const final = score + (isRight ? 1 : 0);
    if (isRight) {
      setScore((s) => s + 1);
      setFeedback("right");
      playRight();
      setTimeout(() => playPitch(current.pitch), 280);
    } else {
      setFeedback("wrong");
      playWrong();
    }
    setTimeout(() => {
      if (idx + 1 >= notes.length) {
        elapsedRef.current = startRef.current ? Date.now() - startRef.current : 0;
        setDone(true);
        if (final === notes.length) playFanfare(0.1, "Daily Challenge");
        updateStreak();
        try { localStorage.setItem("jt_daily_done", date); } catch (e) {}
      } else {
        setIdx((i) => i + 1);
        setFeedback(null);
        setChosen(null);
      }
    }, 700);
  };

  const post = async () => {
    const nm = name.trim();
    if (!nm) {
      toast.error("Enter a name for the leaderboard.");
      return;
    }
    try { localStorage.setItem("jt_trainer_name", nm); } catch (e) {}
    setPosting(true);
    try {
      const res = await api.post("/trainer/daily", {
        name: nm,
        date,
        score,
        total: notes.length,
        time_ms: elapsedRef.current,
      });
      setPostedRank(res.data?.my_rank || 0);
      setRefreshKey((k) => k + 1);
      toast.success(res.data?.my_rank ? `You're #${res.data.my_rank} today!` : "Posted to today's board!");
    } catch (e) {
      toast.error("Couldn't post right now. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 sm:px-8 py-10 lg:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">One set of notes · everyone, everywhere</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Daily Challenge</h1>
        <SoundToggle testId="daily-sound-toggle" className="mt-3" />
        <p className="mt-3 flex items-center gap-3 text-muted-foreground">
          <span className="flex items-center gap-2"><CalendarDays size={16} className="text-primary" /> {prettyDate(date)}</span>
          {streak > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary" data-testid="daily-streak">
              <Flame size={14} /> {streak}-day streak
            </span>
          )}
        </p>

        {!done ? (
          <>
            <div className="mt-8 flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="daily-progress">Note {idx + 1} of {notes.length}</p>
              <p className="font-serif text-lg" data-testid="daily-score">{score} correct</p>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(idx / notes.length) * 100}%` }} />
            </div>

            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className={`relative mt-6 overflow-hidden rounded-3xl border-2 bg-card p-6 sm:p-10 transition-colors ${feedback === "right" ? "border-emerald-500" : feedback === "wrong" ? "border-red-500" : "border-border"}`}
              data-testid="daily-card"
            >
              <p className="mb-2 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{current.clef} clef</p>
              <StaffViewer clef={current.clef} notes={[{ pitch: current.pitch }]} width={420} playOnClick={false} testId="daily-staff" />
              {feedback && (
                <p className={`mt-4 text-center font-serif text-xl ${feedback === "right" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`} data-testid="daily-feedback">
                  {feedback === "right" ? (
                    <span className="inline-flex items-center gap-2"><Check size={20} /> Correct — {letterOf(current.pitch)}!</span>
                  ) : (
                    <span className="inline-flex items-center gap-2"><X size={20} /> It was {letterOf(current.pitch)}.</span>
                  )}
                </p>
              )}
            </motion.div>

            <div className="mt-6 grid grid-cols-4 gap-3 sm:grid-cols-7">
              {LETTERS.map((l) => {
                const correct = letterOf(current.pitch);
                let cls = "border-border bg-card hover:border-primary";
                if (feedback && l === correct) cls = "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
                else if (feedback && l === chosen) cls = "border-red-500 bg-red-500/15 text-red-600 dark:text-red-400";
                return (
                  <button key={l} onClick={() => guess(l)} disabled={!!feedback} data-testid={`daily-answer-${l}`} className={`rounded-xl border-2 py-4 font-serif text-2xl transition-all ${cls}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            {alreadyPlayed && (
              <p className="mt-4 text-center text-xs text-muted-foreground" data-testid="daily-replay-note">You've already posted a time today — play again, but your best time stands.</p>
            )}
          </>
        ) : (
          <div className="mt-8 rounded-3xl border border-border bg-card p-8 text-center" data-testid="daily-result">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"><CalendarDays size={26} /></span>
            <h3 className="mt-5 font-serif text-3xl">Challenge complete</h3>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-background p-5">
                <p className="font-serif text-3xl text-primary" data-testid="daily-final-score">{score}/{notes.length}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Correct</p>
              </div>
              <div className="rounded-2xl bg-background p-5">
                <p className="font-serif text-3xl text-primary" data-testid="daily-final-time">{fmtMs(elapsedRef.current)}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Time</p>
              </div>
            </div>
            {postedRank == null ? (
              <div className="mt-6 flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={40} data-testid="daily-name" className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
                <Button onClick={post} disabled={posting} data-testid="daily-submit" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {posting ? "Posting…" : "Post time"}
                </Button>
              </div>
            ) : (
              <p className="mt-6 text-sm text-primary" data-testid="daily-posted">{postedRank ? `You're #${postedRank} on today's leaderboard!` : "Posted to today's board!"}</p>
            )}
          </div>
        )}

        {/* Streak rewards */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="streak-rewards">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Award size={16} /></span>
            <div>
              <p className="font-serif text-xl">Streak rewards</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {streak > 0 ? `Current streak: ${streak} day${streak === 1 ? "" : "s"}` : "Play daily to build a streak"}
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {STREAK_TIERS.map((t) => {
              const unlocked = rewards.some((r) => r.days === t.days) || streak >= t.days;
              return (
                <div
                  key={t.days}
                  data-testid={`streak-tier-${t.days}`}
                  className={`rounded-2xl border p-4 text-center transition-colors ${unlocked ? "border-primary/40 bg-background" : "border-dashed border-border bg-background/50"}`}
                >
                  <span
                    className="mx-auto flex h-11 w-11 items-center justify-center rounded-full"
                    style={unlocked ? { backgroundColor: t.color + "26", color: t.color } : undefined}
                  >
                    {unlocked ? <Award size={20} style={{ color: t.color }} /> : <Lock size={18} className="text-muted-foreground/50" />}
                  </span>
                  <p className={`mt-2 font-serif text-base leading-tight ${unlocked ? "text-foreground" : "text-muted-foreground/60"}`}>{t.label}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.blurb}</p>
                  {!unlocked && streak > 0 && streak < t.days && (
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`streak-tier-${t.days}-progress`}>{t.days - streak} to go</p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Unlocked rewards are saved to your Badge Gallery.</p>
        </div>

        <DailyLeaderboard date={date} refreshKey={refreshKey} />
      </div>
    </div>
  );
}
